import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useOptionalGuestSession } from "../lib/session";

// The guest app frame from the canvas (v2-g-*): coral header, light sheet, bottom nav.

export type Tab = "discover" | "offers" | "scan" | "cards" | "me" | "none";

export function Shell({
  head,
  nav,
  foot,
  overlay,
  children,
}: {
  head: ReactNode;
  /** The active bottom-nav tab; null hides the nav (focused flows like ordering). */
  nav?: Tab | null;
  /** Floating bar above the nav (the cart bar). */
  foot?: ReactNode;
  /** Bottom sheets and toasts, positioned over the whole frame. */
  overlay?: ReactNode;
  children: ReactNode;
}) {
  const cls = ["g-app", nav !== undefined && nav !== null && "has-nav", foot && "has-foot"].filter(Boolean).join(" ");
  // The page scrolls, not a box inside the frame, so each screen has to start at its own top.
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className={cls}>
      {head}
      <main className="g-sheet">{children}</main>
      {foot && <div className="g-foot">{foot}</div>}
      {/* Phones hide the nav in focused flows (ordering); a tablet always has room for the rail. */}
      {/* Into <body>, not the frame: see .g-nav in styles.css. */}
      {createPortal(<BottomNav tab={nav ?? "none"} wideOnly={nav === undefined || nav === null} />, document.body)}
      {overlay}
    </div>
  );
}

export function Head({
  title,
  sub,
  back,
  icon,
  right,
  big,
}: {
  title: ReactNode;
  sub?: ReactNode;
  back?: () => void;
  /** Store icon (or image URL) in the white square. */
  icon?: string | null;
  right?: ReactNode;
  big?: boolean;
}) {
  return (
    <header className={big ? "g-head big" : "g-head"}>
      {back && (
        <button type="button" className="g-hbtn" aria-label="Back" onClick={back}>
          <span className="ms" aria-hidden="true">arrow_back</span>
        </button>
      )}
      {icon !== undefined && (
        <span className="g-logo">
          {icon && /^(https?:|\/)/.test(icon) ? <img src={icon} alt="" /> : <span className="ms" aria-hidden="true">{icon ?? "storefront"}</span>}
        </span>
      )}
      <div className="g-head-text">
        <b>{title}</b>
        {sub && <small>{sub}</small>}
      </div>
      {right}
    </header>
  );
}

/** The store header every in-store screen shares. `back` defaults to the store's home. */
/** The store's head; a step of the shop flow names itself instead (v2-g-shop-*: "Your bag", "How to pay"). */
export function StoreHead({ back, right, title, sub }: { back?: () => void; right?: ReactNode; title?: string; sub?: string }) {
  const session = useOptionalGuestSession();
  const navigate = useNavigate();
  if (!session) return null;
  const s = session.store;
  return (
    <Head
      icon={title ? undefined : (s.store_icon ?? "storefront")}
      title={title ?? s.name}
      sub={sub ?? ([s.address, s.city].filter(Boolean).join(", ") || "Welcome")}
      back={back ?? (() => navigate({ to: "/store/$storeId", params: { storeId: s.id } }))}
      right={right}
    />
  );
}

function BottomNav({ tab, wideOnly }: { tab: Tab; wideOnly?: boolean }) {
  const session = useOptionalGuestSession();
  const storeId = session?.store.id;
  const item = (t: Tab, icon: string, label: string, to: string) => (
    <Link key={t} to={to as never} className={tab === t ? "on" : undefined}>
      <span className="ms" aria-hidden="true">{icon}</span>
      {label}
    </Link>
  );
  return (
    <nav className={wideOnly ? "g-nav wide-only" : "g-nav"} aria-label="Guest">
      <span className="g-rail-logo" aria-hidden="true">C</span>
      {item("discover", "explore", "Discover", "/")}
      {item("offers", "sell", "Offers", "/offers")}
      <Link to={(storeId ? "/store/$storeId/scan" : "/scan") as never} params={{ storeId } as never} className="g-scan" aria-label="Scan a code">
        <span className="ms" aria-hidden="true">qr_code_scanner</span>
      </Link>
      {item("cards", "wallet", "Cards", "/cards")}
      {item("me", "person", "Me", "/me")}
    </nav>
  );
}

export function Sheet({ onClose, center, label, children }: { onClose: () => void; center?: boolean; label?: string; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="g-dlg-back">
      <button type="button" className="g-dlg-x" aria-label="Close" onClick={onClose} />
      <section role="dialog" aria-modal="true" aria-label={label} className={center ? "g-dlg center" : "g-dlg"}>
        {children}
      </section>
    </div>
  );
}

export function Empty({ icon, title, desc, action }: { icon: string; title: string; desc?: string; action?: ReactNode }) {
  return (
    <div className="g-empty">
      <span className="ms" aria-hidden="true">{icon}</span>
      <b>{title}</b>
      {desc && <p>{desc}</p>}
      {action}
    </div>
  );
}

/** Product image, or the category icon on a tint of the category colour. */
export function Thumb({ image, icon, color, qty }: { image?: string | null; icon?: string | null; color?: string | null; qty?: number }) {
  const c = color ?? "#8A8F9B";
  return (
    <span className="g-thumb" style={{ background: `${c}1f`, color: c }}>
      {image ? <img src={image} alt="" /> : <span className="ms" aria-hidden="true">{icon ?? "restaurant_menu"}</span>}
      {!!qty && <span className="g-qty num">{qty}</span>}
    </span>
  );
}

/** A dark pill at the bottom that clears itself. */
export function useToast(): [ReactNode, (text: string) => void] {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    if (!text) return;
    const t = setTimeout(() => setText(null), 2400);
    return () => clearTimeout(t);
  }, [text]);
  const node = text ? (
    <div className="g-toast" role="status">
      <span className="ms fill" aria-hidden="true">check_circle</span>
      {text}
    </div>
  ) : null;
  return [node, setText];
}

export function tableName(t: { label: string | null; table_number: number } | null | undefined): string {
  if (!t) return "";
  return t.label?.trim() || `Table ${String(t.table_number).padStart(2, "0")}`;
}
